import type Uri from "jsuri";
import includes from "lodash/includes";

export interface IAdditionalOptions {
	dataType?: string;
	externalUrl?: boolean;
	useCookieOnly?: boolean;
}

export const defaultAdditionalOptions: IAdditionalOptions = {
	dataType: "",
	externalUrl: false,
	useCookieOnly: false,
};

const baseParams: RequestInit = {
	credentials: "omit",
	headers: {
		Accept: "application/json",
	},
	signal: null,
};

export const getAuthToken = (): string | undefined => {
	return (window as any).sessionjs?.token;
};

export const getOrgID = (): string => {
	return (window as any).sessionjs?.getuserInfo()?.account_id;
};

export const getUserInfoFromJWT = () => {
	try {
		const token = getAuthToken();
		if (!token) return {};

		const payload = token.split(".")[1];
		const decoded = JSON.parse(atob(payload));
		return {
			username: decoded.preferred_username,
			email: decoded.email,
		};
	} catch {
		return {};
	}
};

const mergeRequestOptions = (target: RequestInit, source: RequestInit) => {
	Object.assign(target, source, {
		headers: {
			...(target.headers as Record<string, string>),
			...(source.headers as Record<string, string>),
		},
	});
};

const errorHandler = (response: Response) => {
	return response.text().then((body) => {
		if (body == null || body === "") {
			const error = new Error(body);
			Object.assign(error, { status: response.status }, { statusText: response.statusText });
			throw error;
		}
		let parsedError;
		try {
			parsedError = JSON.parse(body);
		} catch {
			// non-JSON error body
		}
		if (parsedError) {
			const error = new Error(parsedError?.message || body);
			Object.assign(
				error,
				parsedError,
				{ isApiError: true },
				{ status: response.status },
				{ statusText: response.statusText },
			);
			throw error;
		} else {
			const error = new Error(body);
			Object.assign(error, { status: response.status }, { statusText: response.statusText });
			throw error;
		}
	});
};

const responseHandler = <T>(response: Response, dataType?: string): Promise<T | string> => {
	if (response.ok) {
		const contentType = response.headers.get("content-type");
		if (dataType !== "text" && contentType && includes(contentType, "json")) {
			return response
				.json()
				.then((j) => Promise.resolve(j))
				.catch(() => Promise.resolve({} as T));
		} else if (dataType === "text" || (contentType && includes(contentType, "text"))) {
			return response.text();
		} else {
			return response
				.text()
				.then((j) => Promise.resolve(j ? JSON.parse(j) : {}))
				.catch(() => Promise.resolve({} as T));
		}
	}
	return errorHandler(response);
};

const callFetchAndHandleJwt = async <T>(
	uri: string | Uri,
	params: RequestInit,
	additionalOptions?: IAdditionalOptions,
): Promise<T> => {
	if (!additionalOptions?.externalUrl) {
		const token = getAuthToken();
		if (token && !additionalOptions?.useCookieOnly) {
			(params.headers as Record<string, string>).Authorization = `Bearer ${token}`;
		}
	}
	const response = await fetch(uri.toString(), params);
	return responseHandler<T>(response, additionalOptions?.dataType) as Promise<T>;
};

export const getUri = <T>(
	uri: string | Uri,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = { method: "GET" };
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const postUri = <T>(
	uri: string | Uri,
	body?: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const putUri = <T>(
	uri: string | Uri,
	body?: unknown,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = {
		method: "PUT",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(body),
	};
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};

export const deleteUri = <T>(
	uri: string | Uri,
	extraParams?: RequestInit,
	additionalOptions = defaultAdditionalOptions,
): Promise<T> => {
	const params: RequestInit = { method: "DELETE" };
	mergeRequestOptions(params, baseParams);
	extraParams && mergeRequestOptions(params, extraParams);
	return callFetchAndHandleJwt(uri, params, additionalOptions);
};
